import { useEffect, useState } from "react";

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/public/questions`;
const BEST_TIMES = ["Anytime", "Morning", "Afternoon", "Evening"];
const TIMEZONES = ["Arizona", "Pacific", "Mountain", "Central", "Eastern"];

const detectTz = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (tz.includes("Phoenix")) return "Arizona";
  if (tz.includes("Los_Angeles") || tz.includes("Pacific")) return "Pacific";
  if (tz.includes("Denver") || tz.includes("Boise") || tz.includes("Mountain")) return "Mountain";
  if (tz.includes("Chicago") || tz.includes("Central")) return "Central";
  if (tz.includes("New_York") || tz.includes("Eastern") || tz.includes("Detroit")) return "Eastern";
  return "Arizona";
};

const validUsPhone = (phone) => {
  const digits = phone.replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
};

const inputCls =
  "w-full bg-[#131C31] border border-white/10 px-4 py-3 text-[#f5f3ee] text-sm placeholder:text-[#5c5c58] focus:border-[#5CCBF5] outline-none transition-colors duration-300";
const labelCls = "block font-mono text-[10px] tracking-[0.16em] uppercase text-[#5c5c58] mb-1.5";
const errCls = "mt-1.5 text-xs text-red-400";

export default function QuestionWidget() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", phone: "", question: "", best_time: "Anytime", timezone: "Arizona", company_website: "", consent: false });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success
  const [errorMsg, setErrorMsg] = useState("");
  const [doneName, setDoneName] = useState("");

  useEffect(() => {
    setForm((f) => ({ ...f, timezone: detectTz() }));
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!validUsPhone(form.phone)) e.phone = "Please enter a valid 10-digit US phone number.";
    if (!form.question.trim()) e.question = "Please write your question.";
    if (!form.consent) e.consent = "Please agree to be contacted.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (status === "sending" || !validate()) return;
    setStatus("sending");
    setErrorMsg("");
    const body = {
      first_name: form.first_name.trim(),
      phone: form.phone.trim(),
      question: form.question.trim(),
      best_time: form.best_time,
      timezone: form.timezone,
      consent: true,
      page_url: window.location.origin + window.location.pathname,
      source: "homepage",
      honeypot: form.company_website,
    };
    const leadId = new URLSearchParams(window.location.search).get("lead_id");
    if (leadId) body.lead_id = leadId;
    try {
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.ok) {
          setDoneName(body.first_name);
          setStatus("success");
          return;
        }
        setErrorMsg("Something went wrong. Please try again.");
      } else if (res.status === 429) {
        setErrorMsg("Too many messages — please try again later.");
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
    }
    setStatus("idle");
  };

  return (
    <>
      {open && (
        <div
          data-testid="question-widget-card"
          className="fixed z-[65] bottom-[92px] right-4 left-4 sm:left-auto sm:right-5 sm:w-[360px] max-h-[calc(100vh-120px)] overflow-y-auto border border-white/10 bg-[#0E1526] shadow-2xl shadow-black/50"
        >
          <div className="flex items-start gap-4 p-6 pb-0">
            <span className="w-11 h-11 rounded-full bg-[#131C31] border border-[#5CCBF5]/30 text-[#5CCBF5] display text-xl flex items-center justify-center shrink-0">N</span>
            <div className="flex-1">
              <p className="display text-xl text-[#f5f3ee]">Got a question?</p>
              <p className="text-xs text-[#a7a7a2] mt-1">Nick usually calls back the same day</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="question-widget-close"
              className="text-[#a7a7a2] hover:text-[#5CCBF5] transition-colors duration-300 text-xl leading-none px-1"
            >
              ×
            </button>
          </div>

          {status === "success" ? (
            <p className="p-6 pt-4 text-[#f5f3ee] text-[15px] leading-relaxed" data-testid="question-widget-success">
              Thanks {doneName} — I'll get back to you soon. — Nick
            </p>
          ) : (
            <form onSubmit={submit} noValidate className="p-6 pt-4 relative">
              <p className="text-sm text-[#a7a7a2] leading-relaxed">
                Leave your number and your question. I'll call or text you personally — no bots, no sales pitch.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <label htmlFor="qw-first-name" className={labelCls}>First name *</label>
                  <input id="qw-first-name" data-testid="qw-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                  {errors.first_name && <p className={errCls} data-testid="qw-error-first-name">{errors.first_name}</p>}
                </div>
                <div>
                  <label htmlFor="qw-phone" className={labelCls}>Phone *</label>
                  <input id="qw-phone" data-testid="qw-phone" type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  {errors.phone && <p className={errCls} data-testid="qw-error-phone">{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor="qw-question" className={labelCls}>Your question *</label>
                  <textarea
                    id="qw-question"
                    data-testid="qw-question"
                    rows={3}
                    placeholder="e.g. Would this work for a 3-person plumbing business?"
                    className={`${inputCls} resize-none`}
                    value={form.question}
                    onChange={(e) => set("question", e.target.value)}
                  />
                  {errors.question && <p className={errCls} data-testid="qw-error-question">{errors.question}</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="qw-best-time" className={labelCls}>Best time</label>
                    <select id="qw-best-time" data-testid="qw-best-time" className={inputCls} value={form.best_time} onChange={(e) => set("best_time", e.target.value)}>
                      {BEST_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="qw-timezone" className={labelCls}>Time zone</label>
                    <select id="qw-timezone" data-testid="qw-timezone" className={inputCls} value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
                      {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <input
                  type="text"
                  name="company_website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-0 h-0 opacity-0 pointer-events-none"
                  value={form.company_website}
                  onChange={(e) => set("company_website", e.target.value)}
                />
                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="qw-consent"
                      checked={form.consent}
                      onChange={(e) => set("consent", e.target.checked)}
                      className="mt-0.5 w-4 h-4 shrink-0 accent-[#5CCBF5]"
                    />
                    <span className="text-xs text-[#a7a7a2] leading-relaxed">
                      I agree to be contacted by phone or text about my question. Msg &amp; data rates may apply. Reply STOP to opt out.
                    </span>
                  </label>
                  {errors.consent && <p className={errCls} data-testid="qw-error-consent">{errors.consent}</p>}
                </div>
              </div>
              {errorMsg && <p className={`${errCls} mt-4`} data-testid="qw-error-submit">{errorMsg}</p>}
              <button
                type="submit"
                data-testid="qw-submit"
                disabled={status === "sending"}
                className="mt-6 w-full h-12 bg-[#5CCBF5] text-[#0E1526] font-mono text-[11px] tracking-[0.18em] uppercase hover:bg-[#8adbf8] transition-colors duration-300 disabled:opacity-70 flex items-center justify-center gap-3"
              >
                {status === "sending" && (
                  <span className="w-4 h-4 rounded-full border-2 border-[#0E1526]/30 border-t-[#0E1526] animate-spin" data-testid="qw-spinner" />
                )}
                Send My Question
              </button>
            </form>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="question-widget-toggle"
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-[65] flex items-center gap-2.5 rounded-full bg-[#5CCBF5] text-[#0E1526] h-14 pl-2.5 pr-6 shadow-lg shadow-[#5CCBF5]/25 hover:scale-105 active:scale-95 transition-transform duration-300"
      >
        <span className="w-9 h-9 rounded-full bg-[#0E1526] text-[#5CCBF5] flex items-center justify-center font-mono font-bold">?</span>
        <span className="font-mono text-[11px] tracking-[0.14em] uppercase">Got a question?</span>
      </button>
    </>
  );
}
